/**
 * Preload every image-layer source into an in-memory cache before the canvas
 * draws (the spec's useImageCache). Keyed by the raw layer.src so the renderer
 * can look images up directly. Re-renders the host component as images arrive.
 */
import { useEffect, useRef, useState } from 'react';
import { UPLOAD_URL } from '../../api/api';

export const resolveSrc = (src) => {
  if (!src) return src;
  if (src.startsWith('data:') || src.startsWith('http')) return src;
  // UPLOAD_URL, not API_URL — static uploads live behind the /backend prefix
  // in production while the API is proxied at the web root.
  if (src.startsWith('/uploads')) return `${UPLOAD_URL}${src}`;
  return src;
};

export function useImageCache(layers) {
  const cacheRef = useRef({}); // raw src -> HTMLImageElement
  const [, force] = useState(0);

  const srcKey = layers
    .filter((l) => l.type === 'image' && l.src)
    .map((l) => l.src)
    .join('|');

  useEffect(() => {
    const srcs = [...new Set(srcKey ? srcKey.split('|') : [])];
    let alive = true;
    srcs.forEach((src) => {
      if (cacheRef.current[src]) return;
      const img = new Image();
      const resolved = resolveSrc(src);
      if (!resolved.startsWith('data:')) img.crossOrigin = 'anonymous';
      img.onload = () => { if (alive) { cacheRef.current[src] = img; force((n) => n + 1); } };
      img.onerror = () => {};
      img.src = resolved;
    });
    return () => { alive = false; };
  }, [srcKey]);

  return cacheRef.current;
}
